import { HttpApiAdapter } from '@/data/adapters/HttpApiAdapter';
import { IndexedDBAdapter } from '@/data/adapters/IndexedDBAdapter';
import { LocalStorageAdapter } from '@/data/adapters/LocalStorageAdapter';
import { PersistencePort } from '@/data/ports/PersistencePort';
import { DataSnapshot, CURRENT_SCHEMA } from '@/types/domain';

export type StorageMode = 'local' | 'indexeddb' | 'api';
const MODE_KEY = 'legaltech.storage.mode';

const registry: Record<StorageMode, PersistencePort> = {
  local: LocalStorageAdapter,
  indexeddb: IndexedDBAdapter,
  api: HttpApiAdapter,
};

let active: StorageMode = (localStorage.getItem(MODE_KEY) as StorageMode) || 'local';
export const activePort = () => registry[active];

export async function ensureSnapshot(defaultSnapshot: DataSnapshot): Promise<DataSnapshot> {
  // Try active adapter first
  try {
    const snap = await activePort().get();
    if (snap) return migrateIfNeeded(snap);
  } catch (_) {
    // fall through to fallback chain
  }
  // Fallback to local storage
  try {
    const local = await LocalStorageAdapter.get();
    if (local) return migrateIfNeeded(local);
  } catch (_) {}
  // Fallback to IndexedDB
  try {
    const idb = await IndexedDBAdapter.get();
    if (idb) return migrateIfNeeded(idb);
  } catch (_) {}
  // Final fallback to provided default
  return defaultSnapshot;
}

function migrateIfNeeded(s: DataSnapshot): DataSnapshot {
  if (s.version === CURRENT_SCHEMA) return s;
  return { ...s, version: CURRENT_SCHEMA };
}

export async function switchStorageMode(next: StorageMode, fallbackSnapshot: DataSnapshot) {
  if (next === active) return;
  const from = activePort();
  const to = registry[next];

  const h = await to.health();
  if (!h.ok) throw new Error(`Target adapter not healthy: ${h.details ?? 'n/a'}`);

  const sourceSnap = (await from.get()) ?? fallbackSnapshot;
  const snap = migrateIfNeeded(sourceSnap);

  try {
    await to.put(snap);
    active = next;
    localStorage.setItem(MODE_KEY, next);
  } catch (e) {
    throw new Error(`Migration failed: ${String(e)}`);
  }
}
